/* Per-device persistence: scan progress per purchase order and the last
   order list, so the app opens instantly and a reload never loses a count.
   lib/sync.js shares the progress with the other devices of the login. */

export const safeGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
export const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or private mode: in-memory only */
  }
};
export const safeDel = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

const progressKey = (poId) => `inny.progress.${poId}`;

export const emptyProgress = () => ({ counts: {}, boxes: {}, scans: [], startedAt: null });
export const loadProgress = (poId) => ({ ...emptyProgress(), ...(safeGet(progressKey(poId)) || {}) });
export const saveProgress = (poId, progress) => safeSet(progressKey(poId), progress);
export const clearProgress = (poId) => safeDel(progressKey(poId));

/* What each counted line had open when a confirmation went out, kept until the
   server has answered. After a lost answer the retry still sends these numbers,
   however fresh the screen is, so the server can see it was booked already. */
const confirmKey = (poId) => `inny.confirmSent.${poId}`;
export const loadConfirmSent = (poId) => safeGet(confirmKey(poId)) || {};
export const saveConfirmSent = (poId, sent) => safeSet(confirmKey(poId), sent);
export const clearConfirmSent = (poId) => safeDel(confirmKey(poId));

export const loadOrdersCache = () => safeGet('inny.orders');
export const saveOrdersCache = (data) => safeSet('inny.orders', { data, at: Date.now() });

// Light is the default on the warehouse floor (glare, gloves, quick glances);
// dark stays an explicit choice in the account menu.
export const loadTheme = () => {
  try {
    return localStorage.getItem('inny.theme') || 'light';
  } catch {
    return 'light';
  }
};
export const saveTheme = (theme) => {
  try {
    localStorage.setItem('inny.theme', theme);
  } catch {
    /* ignore */
  }
};

export function applyTheme(theme) {
  const root = document.documentElement;
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && prefersDark);
  if (dark) root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
}

/* Receipts opened on this device, newest first, to jump back in from the
   orders list. One entry per set of orders; a few is plenty. */
const RECENT_KEY = 'inny.recentSessions';
const sessionKey = (s) => [...s.ids].sort((a, b) => a - b).join(',');
export const loadRecentSessions = () => safeGet(RECENT_KEY) || [];
export const rememberSession = ({ ids, names, supplier }) => {
  const key = sessionKey({ ids });
  const rest = loadRecentSessions().filter((s) => sessionKey(s) !== key);
  safeSet(RECENT_KEY, [{ ids, names, supplier, at: Date.now() }, ...rest].slice(0, 8));
};
/** The device's list joined with the login's list from the server: newest wins. */
export const mergeRecentSessions = (fromServer) => {
  const byKey = new Map();
  [...loadRecentSessions(), ...(fromServer || [])].forEach((s) => {
    const k = sessionKey(s);
    if (!byKey.has(k) || (s.at || 0) > (byKey.get(k).at || 0)) byKey.set(k, s);
  });
  const merged = [...byKey.values()].sort((a, b) => (b.at || 0) - (a.at || 0)).slice(0, 8);
  safeSet(RECENT_KEY, merged);
  return merged;
};
